import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchLiveAlertsAPI } from '../services/alertService';

export default function VisitAlertsPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    
    // Strict State Management for Pagination
    const [alerts, setAlerts] = useState([]);
    const [filter, setFilter] = useState('all'); 
    
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Exact Match Search States
    const [searchField, setSearchField] = useState('employeeUsername');
    const [searchValue, setSearchValue] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const parseDate = (val) => {
        if (!val) return new Date();
        if (val.toDate) return val.toDate(); 
        return new Date(val); 
    };

    // Master Fetch Function
    const loadAlerts = async (isLoadMore = false) => {
        if (isLoadMore) setLoadingMore(true);
        else setLoading(true);

        const currentCursor = isLoadMore ? lastDoc : null;
        const searchConfig = isSearching && searchValue.trim() !== '' 
            ? { field: searchField, value: searchValue } 
            : null;
        
        const response = await fetchLiveAlertsAPI(serverId, filter, currentCursor, searchConfig);
        
        if (isLoadMore) {
            setAlerts(prev => [...prev, ...response.data]);
        } else {
            setAlerts(response.data);
        }

        setLastDoc(response.lastVisible);
        setHasMore(response.hasMore);
        
        setLoading(false);
        setLoadingMore(false);
    };

    // Trigger fresh fetch when serverId, filter, or search state changes
    useEffect(() => {
        loadAlerts(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, filter, isSearching]);

    // Search Handlers
    const handleExecuteSearch = (e) => {
        e.preventDefault();
        if (searchValue.trim() === '') return;
        setLastDoc(null);
        setIsSearching(true);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setLastDoc(null);
        setIsSearching(false);
    };

    const getBadgeStyle = (category) => {
        const c = (category || "").toLowerCase();
        if (c === 'visit alerts') return { bg: '#fffaf0', color: '#c05621', border: '#fbd38d', label: 'VISIT ALERT' };
        if (c === 'communications alerts') return { bg: '#ebf8ff', color: '#2b6cb0', border: '#bee3f8', label: 'MESSAGE' };
        if (c === 'safety alerts') return { bg: '#fff5f5', color: '#e53e3e', border: '#feb2b2', label: 'SAFETY' };
        if (c === 'general alerts') return { bg: '#f0fff4', color: '#2f855a', border: '#9ae6b4', label: 'GENERAL' };
        return { bg: '#edf2f7', color: '#4a5568', border: '#e2e8f0', label: 'OTHER' };
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '25px' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>Visit Alerts</h1>
            </div>

            {/* EXACT MATCH SEARCH BLOCK */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', color: '#4a5568', fontSize: '14px' }}>Exact Search:</span>
                <select 
                    value={searchField} 
                    onChange={(e) => setSearchField(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: '#f7fafc', minWidth: '150px' }}
                    disabled={isSearching}
                >
                    <option value="employeeUsername">Staff Username</option>
                    <option value="serviceUserId">Service User ID</option>
                    <option value="relatedVisitId">Visit ID</option>
                </select>
                <form onSubmit={handleExecuteSearch} style={{ display: 'flex', gap: '10px', flex: 1, minWidth: '200px' }}>
                    <input 
                        type="text" 
                        placeholder={`Exact match for ${searchField}...`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        disabled={isSearching}
                        style={{ padding: '8px 12px', flex: 1, borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: isSearching ? '#f7fafc' : 'white' }}
                    />
                    {!isSearching ? (
                        <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Search</button>
                    ) : (
                        <button type="button" onClick={handleClearSearch} style={{ padding: '8px 16px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Clear Search</button>
                    )}
                </form>
            </div>

            {/* FILTERS */}
            <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0', marginBottom: '20px' }}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'visit', label: 'Visits' },
                    { key: 'safety', label: 'Safety' },
                    { key: 'communication', label: 'Communications' },
                    { key: 'general', label: 'General' }
                ].map(f => (
                    <button 
                        key={f.key} 
                        onClick={() => {
                            if (isSearching) handleClearSearch();
                            setFilter(f.key);
                        }}
                        style={{
                            padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                            backgroundColor: filter === f.key && !isSearching ? '#d69e2e' : 'white', 
                            color: filter === f.key && !isSearching ? 'white' : '#4a5568',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s',
                            opacity: isSearching ? 0.5 : 1
                        }}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* LIST */}
            {loading ? <p>Loading alerts...</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {alerts.length > 0 ? alerts.map(item => {
                        const style = getBadgeStyle(item.category);
                        return (
                            <div key={item.id} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: `4px solid ${style.color}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}`, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                        {style.label}
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#a0aec0' }}>
                                        {parseDate(item.notifiedAt).toLocaleString()}
                                    </span>
                                </div>
                                <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#2d3748' }}>
                                    {item.title}
                                </h3>
                                <p style={{ margin: 0, fontSize: '14px', color: '#718096' }}>
                                    {item.notification}
                                </p>
                                
                                {item.actionRequired && (
                                    <div style={{ marginTop: '10px', color: '#c05621', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        ⚠️ Action Required
                                    </div>
                                )}
                            </div>
                        )
                    }) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', backgroundColor: 'white', borderRadius: '8px' }}>
                            <p>No alerts found matching your criteria.</p>
                        </div>
                    )}

                    {/* LOAD MORE LOGIC */}
                    {hasMore && alerts.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
                            <button 
                                onClick={() => loadAlerts(true)}
                                disabled={loadingMore}
                                style={{ padding: '10px 24px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568', fontWeight: 'bold', cursor: loadingMore ? 'not-allowed' : 'pointer' }}
                            >
                                {loadingMore ? 'Loading...' : 'Load Next 50'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}